import React, {
  useEffect, useState, useRef
} from 'react';
import {
  Link, Outlet, useNavigate, useParams, useLocation
} from 'react-router-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import moment from 'moment';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import {
  getSurveyAction, checkForResponseAction, addInfoMessageAction,
  getSurveyResponseTimeLeftAction, clearMessagesAction, addErrorMessageAction
} from '../../actions';
import { routes, SURVEY_TITLE } from '../../constants';
import { getTranslation, withLang, langType } from '../../i18n';
import { autoFocusElementById, getCompiledPath, setDocumentTitle } from '../../util';

const RESPONSE_TIME_WARNING_THRESHOLD = 1000 * 60 * 30;
// Browsers use 32-bit signed integer for Timeout, reverting to 0 for larger values.
const MAX_TIMEOUT = 2147483647; // = 2^32 - 1

const Survey = ({ translate: t, lang }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const params = useParams();

  const surveyTitle = useSelector(((state) => state.survey.surveyContent?.title), {
    equalityFn: shallowEqual
  });
  const surveyParts = useSelector(((state) => state.survey.surveyContent?.surveySchemaParts));
  const extraPart = useSelector(((state) => state.survey.surveyContent?.extraSurveyPart));
  const [alreadyAnswered, setAlreadyAnswered] = useState(false);
  const responseTimeWarningTimeout = useRef(null);
  const responseTimeEndTimeout = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    const checkForResponse = async () => {
      try {
        await dispatch(checkForResponseAction(params.surveyId));
        setAlreadyAnswered(true);
        // API returns 204 if user has responded already and 404 if not.
        // i.e the error is intended behavior (for some reason...)
      } catch (error) {
        if (error.status === 404) {
          await getSurveyContent(params.surveyId);
        }
      }
    };

    checkForResponse();

    // Clean up on unmount
    return () => {
      dispatch(clearMessagesAction());
      clearTimeout(responseTimeWarningTimeout.current);
      clearTimeout(responseTimeEndTimeout.current);
    };
  }, [params.surveyId]);

  useEffect(() => {
    if (location.pathname.split('/').includes('introduction')) {
      autoFocusElementById(SURVEY_TITLE);
    }
  }, [location.pathname]);

  const getTitleTagText = () => {
    // First page, welcome page with the start text
    if (surveyTitle !== null && currentPage === 1) {
      return getTranslation(surveyTitle, lang);
    }

    // Check if we're on one of the following pages and the surveyParts have been loaded
    if (surveyParts && currentPage !== 1) {
      const part = surveyParts[currentPage - 2]; // If the current page is out of range we have gone through all the parts
      if (part) {
        return getTranslation(part.title, lang);
      }
    }

    // If the survey has an extra part, and we're at the end of the normal survey parts
    if (extraPart && currentPage - 2 === surveyParts.length) {
      return getTranslation(extraPart.title, lang);
    }
    // If we're at the second last page, and have not been caught in the other
    // logic means that this has to be the research consent page
    if (currentPage === pageCount - 1) {
      return t('survey.researchConsentTitle');
    }
    // If we're at the last page, this is the one where we thank the respondent for participating
    if (currentPage === pageCount) {
      return t('survey.responseSentTitle');
    }
    return 'UniHow';
  };

  useEffect(() => {
    setDocumentTitle(getTitleTagText());
  }, [surveyTitle, surveyParts, currentPage, lang]);

  const getSurveyContent = async (surveyId) => {
    try {
      await dispatch(getSurveyAction(surveyId));
      navigate(getCompiledPath(routes.survey.introduction, { surveyId }), { replace: true });
      await getAndHandleSurveyResponseTimeLeft(surveyId);
    } catch (error) {
      if (error.status === 404) {
        navigate(routes.error, { state: { errorCode: 404 }, replace: true });
      } else {
        dispatch(addErrorMessageAction(error));
      }
    }
  };

  const getAndHandleSurveyResponseTimeLeft = async (surveyId) => {
    const { value } = await dispatch(getSurveyResponseTimeLeftAction(surveyId));
    setSurveyEndingTimeouts(value);
  };

  const setSurveyEndingTimeouts = (responseTimeData) => {
    if (responseTimeData.remainingResponseTime <= 0) {
      return;
    }

    const remainingResponseTime = Math.min(responseTimeData.remainingResponseTime,
      MAX_TIMEOUT);
    const surveyEndTime = moment(responseTimeData.endTime).format('HH:mm');

    if (remainingResponseTime < RESPONSE_TIME_WARNING_THRESHOLD) {
      triggerResponseTimeWarning(surveyEndTime);
    } else {
      const timeout = remainingResponseTime - RESPONSE_TIME_WARNING_THRESHOLD;

      responseTimeWarningTimeout.current = setTimeout(
        () => triggerResponseTimeWarning(surveyEndTime), timeout
      );
    }

    responseTimeEndTimeout.current = setTimeout(() => responseTimeEnded(), remainingResponseTime);
  };

  const responseTimeEnded = async () => {
    try {
      const { value } = await dispatch(getSurveyResponseTimeLeftAction(params.surveyId));
      if (value.remainingResponseTime <= 0) {
        dispatch(clearMessagesAction());
        navigate(routes.surveyTimeout);
      } else {
        setSurveyEndingTimeouts(value);
      }
    } catch (error) {
      dispatch(addErrorMessageAction(error));
    }
  };

  const triggerResponseTimeWarning = (endTime) => {
    const message = t('surveyResponseTimeEnd.warning', { endTime });
    dispatch(addInfoMessageAction(message));
  };

  const renderAlreadyAnsweredMessage = () => (
    <div>
      <p>{t('responseMessage.responded')}</p>
      <Link className="button--outline" to={routes.index}>
        {t('common.toFrontPage')}
      </Link>
    </div>
  );

  return surveyTitle ? (
    <TransitionGroup>
      <CSSTransition key={location.pathname} classNames="fade" timeout={300} exit={false}>
        <div className="page-container">
          {alreadyAnswered && renderAlreadyAnsweredMessage()}
          {!alreadyAnswered && (
          <div>
            <div className="flex-inline">
              <h1 className="page-title--small" id={SURVEY_TITLE}>
                {t('frontPage.surveyBoxList.answerSurvey')}: {getTranslation(surveyTitle, lang)} - {t('common.page')} {currentPage}/{pageCount}
              </h1>
            </div>
            <Outlet context={[setCurrentPage, setPageCount]} />
          </div>
          )}
        </div>
      </CSSTransition>
    </TransitionGroup>
  ) : null;
};

Survey.propTypes = {
  ...langType
};

export default withLang(Survey);
